// @ts-nocheck
/**
 * generateQrSvg.ts — Sync QR matrix → SVG markup for layout `qrCode` (DEC-082).
 *
 * Uses `qrcode.create` (pure JS, no canvas). Empty/invalid payloads return null
 * so callers can paint a placeholder. Colors are CSS strings with optional alpha.
 */

import QRCode from 'qrcode';
import type { QrCodeErrorCorrection } from '../../types/component';
import { QR_CODE_ERROR_CORRECTION_LEVELS } from '../../types/component';

export const DEFAULT_QR_MODULE_COLOR = '#000000FF';
export const DEFAULT_QR_BACKGROUND_COLOR = '#FFFFFFFF';
export const DEFAULT_QR_ERROR_CORRECTION: QrCodeErrorCorrection = 'M';

export function isQrColorString(value: string): boolean {
  if (/^#([0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(value)) return true;
  if (/^rgba?\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*(,\s*[\d.]+\s*)?\)$/.test(value)) return true;
  return false;
}

export function normalizeQrErrorCorrection(raw: unknown): QrCodeErrorCorrection {
  if (typeof raw === 'string' && QR_CODE_ERROR_CORRECTION_LEVELS.includes(raw as QrCodeErrorCorrection)) {
    return raw as QrCodeErrorCorrection;
  }
  return DEFAULT_QR_ERROR_CORRECTION;
}

function escapeXmlAttr(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function coercePayload(raw: unknown): string | null {
  if (raw == null) return null;
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return String(raw);
  }
  return null;
}

export interface GenerateQrSvgOptions {
  color?: string;
  backgroundColor?: string;
  errorCorrection?: QrCodeErrorCorrection;
}

/**
 * Returns SVG markup for `payload`, or null when the payload is empty/unencodable.
 */
export function generateQrSvg(payload: unknown, options: GenerateQrSvgOptions = {}): string | null {
  const text = coercePayload(payload);
  if (text == null) return null;

  const color = options.color && isQrColorString(options.color)
    ? options.color
    : DEFAULT_QR_MODULE_COLOR;
  const backgroundColor =
    options.backgroundColor && isQrColorString(options.backgroundColor)
      ? options.backgroundColor
      : DEFAULT_QR_BACKGROUND_COLOR;
  const errorCorrection = normalizeQrErrorCorrection(options.errorCorrection);

  try {
    const qr = QRCode.create(text, { errorCorrectionLevel: errorCorrection });
    const size = qr.modules.size;
    const rects: string[] = [];
    for (let row = 0; row < size; row++) {
      for (let col = 0; col < size; col++) {
        if (qr.modules.get(row, col)) {
          rects.push(`<rect x="${col}" y="${row}" width="1" height="1"/>`);
        }
      }
    }
    return (
      `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${size} ${size}" ` +
      `shape-rendering="crispEdges">` +
      `<rect width="${size}" height="${size}" fill="${escapeXmlAttr(backgroundColor)}"/>` +
      `<g fill="${escapeXmlAttr(color)}">${rects.join('')}</g>` +
      `</svg>`
    );
  } catch {
    return null;
  }
}
